import { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { axiosReq, axiosInstance } from "../api/axiosDefaults"
import useHostName from "./useHostName"
import { useCurrentUser } from "../contexts/CurrentUserContext"
import { CLIENT_PROGRAM_HOSTNAME } from "../utils/config"

/** Matches backend `DEPARTMENT_CHOICES` (slug → display label). */
export const DEPARTMENT_CHOICES = [
  { value: "camera", label: "Camera" },
  { value: "script", label: "Script" },
  { value: "art", label: "Art" },
  { value: "make-up", label: "Hair/Makeup" },
  { value: "wardrobe", label: "Wardrobe" },
  { value: "location", label: "Location" },
  { value: "sound", label: "Sound" },
  { value: "casting", label: "Casting" },
  { value: "post", label: "Post/VXS" },
  { value: "production", label: "Production" },
  { value: "stunts", label: "Stunts/SFX" },
  { value: "electric", label: "Electric/Grip" },
  { value: "universal", label: "Universal" },
]

function parseProjectCategoryType(raw) {
  if (raw == null || raw === "") return null
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  }
  return raw
}

/** For Television include `episodeId` from `?episode=`. */
function buildScenesQuery(currentUser, locationSearch) {
  const projectType = parseProjectCategoryType(currentUser?.project_category_type)
  const params = new URLSearchParams()
  if (projectType === "Television") {
    const episodeId = new URLSearchParams(locationSearch || "").get("episode")
    if (episodeId != null && episodeId !== "") {
      params.set("episodeId", episodeId)
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

/**
 * `departments`: static `DEPARTMENT_CHOICES` (`value` + `label`).
 * `scenes`: GET `/scenes/` (same host rules as elsewhere in the app).
 */
export default function useDepartmentPostsDepartments() {
  const host = useHostName()
  const location = useLocation()
  const currentUser = useCurrentUser()
  const departments = useMemo(
    () => DEPARTMENT_CHOICES.map(({ value, label }) => ({ value, label })),
    []
  )
  const [scenes, setScenes] = useState({ results: [] })

  useEffect(() => {
    const scenesQuery = buildScenesQuery(currentUser, location.search)
    const scenesPath =
      (host === CLIENT_PROGRAM_HOSTNAME
        ? "/scenes/"
        : `${localStorage.getItem("projectSlug")}/scenes/`) + scenesQuery

    const run = async () => {
      const scenesReq =
        host === CLIENT_PROGRAM_HOSTNAME
          ? axiosReq.get(scenesPath)
          : axiosInstance.get(scenesPath, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
            withCredentials: true,
          })

      try {
        const scenesRes = await scenesReq
        const scenesData = scenesRes?.data
        setScenes(
          scenesData && typeof scenesData === "object"
            ? scenesData
            : { results: [] }
        )
      } catch (e) {
        console.log(e)
        setScenes({ results: [] })
      }
    }

    run()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    host,
    location.search,
    currentUser?.project_id,
    currentUser?.project_category_type,
  ])
  return { departments, scenes }
}
